import { systemCode, xs_languageCode } from "../..";
import { useContext, useRef, useState } from "react";
import { useEffect } from "react";
import { getConfiguration } from "../utils";
import { BetslipContext } from "../betslip/BetslipConf";

const responsesCache = new Map();

function serveFromCache(url) {
    // Remove stale entries
    let minTs = new Date();
    minTs.setMinutes(minTs.getMinutes() - 5);
    minTs = +minTs;
    const staleIds = new Set();
    responsesCache.forEach((entry, id) => {
        if (entry.ts < minTs) {
            staleIds.add(id);
        }
    });
    if (staleIds.size > 0) {
        staleIds.forEach(id => responsesCache.delete(id));
    }

    if (responsesCache.has(url)) {
        return responsesCache.get(url).data;
    }

    return null;
}

function getFullUrl(url, params) {
    let fullUrl = '', requestParams = null;
    if (params?.skipBaseParams === true) {
        delete params.skipBaseParams;
        requestParams = params;
    } else {
        const baseParams = {
            systemCode: systemCode,
            lingua: xs_languageCode,
            hash: ''
        };
        requestParams = Object.assign(baseParams, params);
    }
    fullUrl = `${url}?${new URLSearchParams(requestParams)}`;


    return fullUrl;
}

export function useFetchNew(url, params, callback, shouldExecute = true) {
    const state = useRef({
        url: null,
        isLoading: true,
        isError: false,
        data: null
    }).current;
    const [render, setRender] = useState(false);

    if (!shouldExecute) {
        return [false, false, null];
    }

    url = getFullUrl(url, params);

    if (url != null && url == state.url) {
        return [state.isLoading, state.isError, state.data];
    }

    const response = serveFromCache(url);
    if (response != null) {
        state.isLoading = false;
        state.isError = false;
        state.url = new String(url);
        state.data = callback(response);
    } else {
        state.isLoading = true;
        state.isError = false;
        state.data = null;
        state.url = null;

        fetch(url)
            .then(response => response.json())
            .then(response => {
                responsesCache.set(url, {
                    ts: +new Date(),
                    data: response
                });
                state.isLoading = false;
                state.isError = false;
                state.data = callback(response);
                state.url = new String(url);
                setRender(!render);
            }).catch(e => {
                console.error(e);
                state.isLoading = false;
                state.isError = true;
                state.data = null;
                state.url = new String(url);
                setRender(!render);
            });
    }
    
    return [state.isLoading, state.isError, state.data];
}

export function useFetch(url, params, callback, shouldExecute = true) {
    const doFetch = useRef(null);
    const [state, setState] = useState({
        isLoading: true,
        isError: false,
        data: null
    });

    let fullUrl = '', requestParams = null;
    if (params?.skipBaseParams === true) {
        delete params.skipBaseParams;
        requestParams = params;
    } else {
        const baseParams = {
            systemCode: systemCode,
            lingua: xs_languageCode,
            hash: ''
        };
        requestParams = Object.assign(baseParams, params);
    }
    fullUrl = `${url}?${new URLSearchParams(requestParams)}`;
    
    useEffect(() => {
        if (!shouldExecute) {
            return;
        }

        if (!doFetch.current) {
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        fetch(fullUrl, { signal })
            .then(response => response.json())
            .then(response => {
                responsesCache.set(fullUrl, {
                    ts: +new Date(),
                    data: response
                });
                setState({
                    isLoading: false,
                    isError: false,
                    data: callback(response),
                    fullUrl
                });
            }).catch(e => {
                console.error(e);
                // console.error(`${fullUrl}: ${e}`);
                if (e.name !== 'AbortError') {
                    setState({
                        isLoading: false,
                        isError: true,
                        data: null,
                        fullUrl
                    });
                }
            });

        return () => controller.abort();
    }, [fullUrl]);

    if (state.fullUrl !== fullUrl) {
        const response = serveFromCache(fullUrl);
        if (response != null) {
            doFetch.current = false;
            return [false, false, callback(response)];
        } else {
            doFetch.current = true;
        }
    }

    return [state.isLoading, state.isError, state.data];
}

function poll(fullUrl, callback, setState, signal, attempt) {
    fetch(fullUrl, { signal })
        .then(response => response.json())
        .then(response => {
            const data = callback(response);
            setState({
                isLoading: false,
                isError: false,
                data
            });
            attempt.current = 1;
        }).catch(e => {
            console.error(e);
            if (e.name !== 'AbortError') {
                if (attempt.current === 2) {
                    // console.error(`${fullUrl}: ${e}`);
                    setState({
                        isLoading: false,
                        isError: true,
                        data: null
                    });
                }
                attempt.current++;
            }
            console.log(e);
        });
}

export function usePoll(url, params, callback) {
    const conf = getConfiguration();
    const ctxs = useRef({}).current;
    ctxs.betslipContext =  useContext(BetslipContext);
    const [state, setState] = useState({
        isLoading: true,
        isError: false,
        data: null
    });
    const attempt = useRef(1);

    url = getFullUrl(url, params);
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        poll(url, callback, setState, signal, attempt);
        const intervalId = setInterval(() => {
            if(!ctxs?.betslipContext?.betInProgress) {
                poll(url, callback, setState, signal, attempt);
            }
        }, conf.lpo);

        return () => {
            clearInterval(intervalId);
            controller.abort();
        };
    }, [url]);

    return [state.isLoading, state.isError, state.data];
}
